import { PUMABILT_IMAGE } from "@/data/content";

export default function Pumabilt() {
  const palette = [
    { name: "Bone", hex: "#D6C7AE" },
    { name: "Obsidian", hex: "#0E0E0E" },
    { name: "Warm Brass", hex: "#B08D46" },
    { name: "Dusty Lavender", hex: "#B7A6C8" },
  ];

  const specs = [
    ["Make", "Peterbilt 359"],
    ["Year", "1979"],
    ["Scale", "Half-Scale Restoration"],
    ["Finish", "Hand-painted, ten coats"],
    ["Trim", "Solid brass, hand-engraved"],
    ["Companion", "One brass-cast house puma"],
  ];

  return (
    <div data-testid="pumabilt-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="label-eyebrow text-lavender">N° 01 — The Mascot</p>
            <h1 className="font-display text-bone text-5xl md:text-7xl lg:text-[100px] mt-5 leading-[0.92]">
              The <em className="not-italic text-brass">Pumabilt</em>.
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="font-display italic text-bone/70 text-xl md:text-2xl leading-snug">
              A half-scale Peterbilt, meticulously restored by hand. Built to turn heads, spark conversation, and welcome you to The House. A West Coast icon in the making.
            </p>
          </div>
        </div>

        <div className="mt-16 relative aspect-square md:aspect-[4/3] overflow-hidden border border-bone/10 bg-obsidian">
          <img src={PUMABILT_IMAGE} alt="The Pumabilt" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-x-0 bottom-0 h-1/4 scrim-bottom pointer-events-none" />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 text-bone/90">
            <p className="label-eyebrow">N° 042 / 111</p>
            <p className="font-display italic mt-1">Hand-finished, brass nameplate</p>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-12 gap-10 md:gap-16" data-testid="pumabilt-story">
          <div className="md:col-span-8 space-y-14">
            <section>
              <p className="label-eyebrow">Her Story</p>
              <p className="font-display text-bone text-2xl md:text-3xl mt-5 leading-[1.25]">
                Pumabilt wasn&apos;t built to be ordinary. She was brought back to life.
              </p>
              <p className="mt-6 text-bone/75 leading-relaxed">
                Inspired by the iconic 1979 Peterbilt 359, Pumabilt is a custom half-scale tribute to one of America&apos;s
                most recognizable trucks. Every rivet, every panel, every stitch, and every finish was chosen with
                intention to create something that feels both familiar and entirely original.
              </p>
              <p className="mt-6 font-display italic text-bone/85 text-xl md:text-2xl leading-snug">
                She wasn&apos;t designed to haul freight.
              </p>
              <p className="font-display italic text-bone text-xl md:text-2xl leading-snug">
                She was built to carry <span className="text-brass">The Uncanny House</span>.
              </p>
            </section>

            <section>
              <p className="label-eyebrow">Her Finish</p>
              <p className="mt-5 text-bone/75 leading-relaxed">
                Finished in bone with obsidian accents and warm brass details, Pumabilt blends timeless industrial
                design with refined craftsmanship. Inside, quilted leather, brass instrumentation, and thoughtful
                details transform the cab into an extension of The House itself.
              </p>
              <p className="mt-6 font-display italic text-bone/85 text-xl md:text-2xl leading-snug">
                Every surface serves a purpose.
              </p>
              <p className="font-display italic text-bone/85 text-xl md:text-2xl leading-snug">
                Every detail tells a story.
              </p>
            </section>

            <section>
              <p className="label-eyebrow">Her Spirit</p>
              <p className="font-display text-bone text-2xl md:text-3xl mt-5 leading-[1.25]">
                Pumabilt is more than a truck.
              </p>
              <div className="mt-6 space-y-2 font-display italic text-bone/80 text-lg md:text-xl leading-[1.5]">
                <p>She is the first impression.</p>
                <p>The conversation before the coffee.</p>
                <p>The photograph people stop to take.</p>
                <p>The reason someone walks across the parking lot to see what&apos;s waiting inside.</p>
              </div>
              <p className="mt-8 font-display text-bone text-2xl md:text-3xl leading-[1.2]">
                She is <em className="not-italic text-brass">The Face of The Uncanny House</em>.
              </p>

              <p className="mt-10 font-display italic text-lavender text-xl md:text-2xl leading-snug">
                Built for beauty. Designed for flow. Born from passion.
              </p>

              <p className="mt-14 text-bone/70 leading-relaxed max-w-2xl">
                Every house has a foundation. Every legacy begins with a second chance.
                <span className="text-bone"> Pumabilt is both.</span>
              </p>
            </section>
          </div>

          <div className="md:col-span-4">
            <p className="label-eyebrow">Specification</p>
            <ul className="mt-5 divide-y divide-bone/10" data-testid="pumabilt-specs">
              {specs.map(([k, v]) => (
                <li key={k} className="py-4 flex items-center justify-between gap-6">
                  <span className="text-bone/55 text-sm uppercase tracking-[0.24em]">{k}</span>
                  <span className="font-display text-bone text-lg text-right">{v}</span>
                </li>
              ))}
            </ul>

            <p className="label-eyebrow mt-12">Finish — Palette</p>
            <div className="mt-5 grid grid-cols-2 gap-3" data-testid="pumabilt-palette">
              {palette.map((c) => (
                <div key={c.name} className="border border-bone/10 p-3">
                  <div className="aspect-square w-full" style={{ backgroundColor: c.hex }} />
                  <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-bone/70">{c.name}</p>
                  <p className="font-display text-bone/50 text-sm">{c.hex}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
